import React from 'react';
import { Steps, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { CheckOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { STEP_CONFIG } from '../constants';
import SelectedValuesTags from './SelectedValuesTags';

/**
 * StepWizard Component
 * Displays the 4-step progress indicator for the Risk Calculator
 * Following Figma design specifications with custom styling
 * Now includes selected values as tags under each step title
 *
 * @param {Object} props
 * @param {number} props.currentStep - Current active step (1-4)
 * @param {Function} props.onStepClick - Callback when a step is clicked
 * @param {Function} props.isStepValid - Function to check if a step is valid
 * @param {Object} props.selections - All selections object (IDs only)
 * @param {Object} props.enrichedData - Enriched data with labels
 * @param {Object} props.stepWarnings - Object with step keys as keys and warning messages as values
 */
const StepWizard = React.memo(
  ({
    currentStep,
    onStepClick,
    isStepValid,
    selections = {},
    enrichedData = {},
    stepWarnings = {},
  }) => {
    const { t } = useTranslation('riskCalculator');

    const handleStepChange = (stepIndex) => {
      const stepKey = stepIndex + 1;
      if (stepKey <= currentStep || isStepValid(currentStep)) {
        onStepClick(stepKey);
      }
    };

    const items = STEP_CONFIG.map((step) => {
      const isActive = step.key === currentStep;
      const isCompleted = step.key < currentStep;
      const isDisabled = step.key > currentStep;
      const hasWarning = stepWarnings[step.key];

      return {
        title: (
          <div>
            <div
              className={`
              inline-flex items-center gap-2 lg:gap-3 px-2 lg:px-4 py-1.5 lg:py-2 transition-all duration-300
              ${isActive || isCompleted ? 'bg-[#E9F1D9]' : 'bg-[#F5F5F5]'}
              ${!isDisabled ? 'cursor-pointer hover:bg-[#E9F1D9]/50' : 'cursor-default'}
            `}
              style={{ borderRadius: '16px' }}
            >
              {/* Circle inside title */}
              <div
                className="flex items-center justify-center flex-shrink-0 transition-all duration-300 w-6 h-6 lg:w-8 lg:h-8 text-xs lg:text-base"
                style={{
                  borderRadius: '32px',
                  backgroundColor:
                    isActive || isCompleted ? '#105235' : 'transparent',
                  border:
                    isActive || isCompleted
                      ? 'none'
                      : '1px solid rgba(0, 0, 0, 0.25)',
                  color:
                    isActive || isCompleted ? 'white' : 'rgba(0, 0, 0, 0.45)',
                  fontFamily: 'Roboto',
                  fontWeight: 400,
                  lineHeight: '24px',
                  textAlign: 'center',
                }}
              >
                {isCompleted ? (
                  <CheckOutlined className="text-[10px] lg:text-sm" />
                ) : (
                  step.key
                )}
              </div>

              <span
                className="text-xs lg:text-base font-bold transition-all duration-300"
                style={{
                  fontFamily: 'Roboto',
                  lineHeight: '24px',
                  color:
                    isActive || isCompleted ? '#105235' : 'rgba(0, 0, 0, 0.45)',
                }}
              >
                {t(step.titleKey)}
              </span>

              {/* Warning icon when step has mismatch */}
              {hasWarning && (
                <Tooltip title={hasWarning} placement="top">
                  <ExclamationCircleOutlined
                    style={{
                      fontSize: '14px',
                      color: '#d48806',
                      marginLeft: '4px',
                    }}
                  />
                </Tooltip>
              )}
            </div>
          </div>
        ),
        description: (
          <div className="pl-2">
            <SelectedValuesTags
              step={step.key}
              selections={selections}
              enrichedData={enrichedData}
            />
          </div>
        ),
        status: isCompleted ? 'finish' : isActive ? 'process' : 'wait',
        icon: <span />,
        disabled: isDisabled && !isStepValid(currentStep),
      };
    });

    return (
      <div className="step-wizard-container">
        {/* Desktop: Horizontal layout */}
        <div className="hidden md:block">
          <Steps
            current={currentStep - 1}
            onChange={handleStepChange}
            items={items}
            className="risk-calculator-steps"
          />
        </div>

        {/* Mobile: Vertical stacked layout */}
        <div className="flex md:hidden flex-col gap-4">
          {STEP_CONFIG.map((step) => {
            const isActive = step.key === currentStep;
            const isCompleted = step.key < currentStep;
            const isDisabled = step.key > currentStep;
            const isClickable = !isDisabled || isStepValid(currentStep);
            const hasWarning = stepWarnings[step.key];

            return (
              <div key={step.key}>
                <div
                  onClick={() => !isDisabled && onStepClick(step.key)}
                  className={`
                  inline-flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 mb-2
                  transition-all duration-300
                  ${isActive || isCompleted ? 'bg-[#E9F1D9]' : 'bg-[#F5F5F5]'}
                  ${isClickable ? 'cursor-pointer hover:bg-[#E9F1D9]/50' : 'cursor-default'}
                `}
                  style={{ borderRadius: '16px' }}
                >
                  <div
                    className="flex items-center justify-center flex-shrink-0 transition-all duration-300 w-6 h-6 sm:w-7 sm:h-7 text-xs sm:text-sm"
                    style={{
                      borderRadius: '32px',
                      backgroundColor:
                        isActive || isCompleted ? '#105235' : 'transparent',
                      border:
                        isActive || isCompleted
                          ? 'none'
                          : '1px solid rgba(0, 0, 0, 0.25)',
                      color:
                        isActive || isCompleted
                          ? 'white'
                          : 'rgba(0, 0, 0, 0.45)',
                      fontFamily: 'Roboto',
                      fontWeight: 400,
                      lineHeight: '20px',
                      textAlign: 'center',
                    }}
                  >
                    {isCompleted ? (
                      <CheckOutlined className="text-[10px] sm:text-xs" />
                    ) : (
                      step.key
                    )}
                  </div>
                  <span
                    className="text-xs sm:text-sm font-bold transition-all duration-300"
                    style={{
                      fontFamily: 'Roboto',
                      lineHeight: '20px',
                      color:
                        isActive || isCompleted
                          ? '#105235'
                          : 'rgba(0, 0, 0, 0.45)',
                    }}
                  >
                    {t(step.titleKey)}
                  </span>

                  {/* Warning icon when step has mismatch */}
                  {hasWarning && (
                    <Tooltip title={hasWarning} placement="top">
                      <ExclamationCircleOutlined
                        style={{
                          fontSize: '14px',
                          color: '#d48806',
                          marginLeft: '4px',
                        }}
                      />
                    </Tooltip>
                  )}
                </div>
                <div className="pl-2">
                  <SelectedValuesTags
                    step={step.key}
                    selections={selections}
                    enrichedData={enrichedData}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

StepWizard.displayName = 'StepWizard';

export default StepWizard;
